import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Lock, ArrowRight, Loader2, Check } from "lucide-react";
import { MEMBERS } from "@/constants/testIds";
import { PayPalButton } from "./PayPalButton";
import { ConsentCheckbox } from "./ConsentCheckbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SubscriptionModal = ({ trigger, onClose }) => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState("monthly");

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/subscription/plans`)
      .then(({ data }) => {
        if (!mounted) return;
        setPlans(data.plans || []);
      })
      .catch(() => {
        // Non-fatal — the form still works on default plan; just hide the toggle.
      });
    return () => {
      mounted = false;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please add your first name and a valid email.");
      return;
    }
    if (!consent) {
      toast.error("Please tick the acknowledgement to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/subscription/checkout`, {
        email: email.trim(),
        first_name: firstName.trim(),
        origin_url: window.location.origin,
        plan,
      });
      window.location.href = data.url;
    } catch (err) {
      setLoading(false);
      toast.error("Couldn't start checkout — please try again.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[var(--ink)]/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid={MEMBERS.modal}
    >
      <div
        className="w-full max-w-md bg-[var(--paper)] rounded-3xl border border-[var(--line)] p-7 md:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <span className="num-tag text-xs">Members</span>
            <h3 className="font-display text-2xl font-extrabold mt-2 leading-tight">
              Join the Ditch Collective
            </h3>
            {trigger?.title && (
              <p className="text-xs text-[var(--ocean)] mt-2">
                Starting with: <span className="font-semibold">{trigger.title}</span>
              </p>
            )}
          </div>
          <button
            type="button"
            data-testid={MEMBERS.modalClose}
            onClick={onClose}
            className="shrink-0 p-2 rounded-full hover:bg-[var(--mist-soft)] transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <ul className="space-y-2 mb-5 text-sm text-[var(--ink)]">
          {[
            "All our guides on tap — rental, jobs, tax, schools, the lot",
            "New drops added every month",
            "Cancel any time from your dashboard",
          ].map((line) => (
            <li key={line} className="flex items-start gap-2">
              <Check size={16} className="mt-0.5 text-[var(--lime-deep)] shrink-0" />
              <span>{line}</span>
            </li>
          ))}
        </ul>

        {plans.length > 1 && (
          <div
            className="grid grid-cols-2 gap-2 mb-5"
            data-testid={MEMBERS.planToggle}
            role="radiogroup"
            aria-label="Billing plan"
          >
            {plans.map((p) => {
              const active = plan === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  data-active={active}
                  data-testid={MEMBERS.planOption(p.id)}
                  onClick={() => setPlan(p.id)}
                  className={`text-left rounded-2xl border px-4 py-3 transition-all ${
                    active
                      ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]"
                      : "border-[var(--line)] bg-white hover:border-[var(--ink)]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-widest font-bold">
                      {p.label}
                    </span>
                    {p.id === "annual" && (
                      <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-[var(--lime)] text-[var(--ink)]" : "bg-[var(--lime)] text-[var(--ink)]"}`}>
                        Save 2 mo
                      </span>
                    )}
                  </div>
                  <p className="font-display font-extrabold text-base mt-1 leading-tight">
                    {p.blurb}
                  </p>
                </button>
              );
            })}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[var(--ocean)] mb-1.5">
              First name
            </label>
            <input
              data-testid={MEMBERS.firstName}
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Your first name"
              className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base focus:outline-none focus:border-[var(--ink)] transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[var(--ocean)] mb-1.5">
              Email — we'll send your members link here
            </label>
            <input
              data-testid={MEMBERS.email}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base focus:outline-none focus:border-[var(--ink)] transition-colors"
            />
          </div>

          <ConsentCheckbox checked={consent} onChange={setConsent} testId="subscription-consent" />

          <button
            type="submit"
            data-testid={MEMBERS.submit}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-display font-bold rounded-xl px-6 py-3.5 hover:bg-[var(--ink-soft)] transition-colors disabled:opacity-60"
          >
            {loading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <>
                Continue to secure checkout <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>

        <PayPalButton
          kind="subscription"
          email={email}
          firstName={firstName}
          plan={plan}
          disabled={!firstName.trim() || !/^\S+@\S+\.\S+$/.test(email) || !consent || loading}
          onSuccess={() => {
            window.location.href = "/members?paypal=success";
          }}
        />

        <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-[var(--ocean)]">
          <Lock size={12} /> Secure checkout by Stripe or PayPal · Cancel any time
        </p>
      </div>
    </div>
  );
};
